/**
 * Presentational section for browsing thematic categories.
 * Mock data has been replaced by backend taxonomy data; regions stay untouched here.
 */
import React, { Fragment, useMemo, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
} from "../ui/breadcrumb";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Skeleton } from "../ui/skeleton";
import { FolderTree, ArrowLeft, ChevronsDown, Search } from "lucide-react";
import { categoryIconMap } from "../../utils/generatedCategoryIcons";

export interface ThematicCategoryNode {
  slug: string;
  name: string;
  nameEn?: string;
  nameDe?: string;
  iconName?: string | null;
  count?: number;
  subcategories?: ThematicCategoryNode[];
}

interface ThematicFilterSectionProps {
  categoryPath: string[];
  currentCategory: ThematicCategoryNode | null;
  currentSubcategories: ThematicCategoryNode[];
  showingSubcategories: boolean;
  thematicCategories: ThematicCategoryNode[];
  handleCategoryClick: (categorySlug: string) => void;
  handleCategoryPathSelect: (categoryPathSlugs: string[]) => void;
  handleViewResults: () => void;
  handleBreadcrumbClick: (index: number) => void;
  t: (key: string) => string;
  sectionMaxHeightPx?: number | null;
  isLoading?: boolean;
}

const normalizeText = (value: string | null | undefined): string =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export const ThematicFilterSection: React.FC<ThematicFilterSectionProps> = ({
  categoryPath,
  currentCategory,
  currentSubcategories,
  showingSubcategories,
  thematicCategories,
  handleCategoryClick,
  handleCategoryPathSelect,
  handleViewResults,
  handleBreadcrumbClick,
  t,
  sectionMaxHeightPx,
  isLoading = false,
}) => {
  type ThematicSearchOption = {
    id: string;
    category: ThematicCategoryNode;
    pathSlugs: string[];
    pathNames: string[];
    searchText: string;
    labelNormalized: string;
    pathNormalized: string;
  };

  const leafTitle = t("categories.lastSubcategorySelectedTitle");
  const leafDescTemplate =
    t("categories.lastSubcategorySelectedDescription") ||
    t("categories.lastSubcategorySelectedTitle");
  const leafDescription = leafDescTemplate.replace(
    "{{category}}",
    currentCategory?.name ?? t("categories.thematic")
  );
  const showLeafInfo =
    !!currentCategory && !(currentCategory.subcategories?.length ?? 0);
  const [thematicQuery, setThematicQuery] = useState("");
  const titleTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const bodyTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const actionButtonStyle = {
    "--action-bg": "color-mix(in srgb, var(--accent) 14%, transparent)",
    "--action-hover": "color-mix(in srgb, var(--accent) 30%, transparent)",
    "--action-border": "color-mix(in srgb, var(--accent) 35%, var(--foreground))",
  } as React.CSSProperties;
  const breadcrumbStyle = {
    "--breadcrumb-text":
      "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
    "--breadcrumb-hover": "var(--primary)",
  } as React.CSSProperties;

  const constrainedSection =
    typeof sectionMaxHeightPx === "number" && sectionMaxHeightPx > 0;
  // Mobile/stacked mode has no peer-height constraint; keep the category list compact.
  const stackedMobileListStyle = {
    maxHeight: "clamp(14rem, 42svh, 24rem)",
  } as React.CSSProperties;

  const thematicSearchOptions = useMemo(() => {
    const options: ThematicSearchOption[] = [];

    const collect = (
      nodes: ThematicCategoryNode[],
      parentSlugs: string[],
      parentNames: string[]
    ) => {
      nodes.forEach((node) => {
        const pathSlugs = [...parentSlugs, node.slug];
        const pathNames = [...parentNames, node.name];
        options.push({
          id: pathSlugs.join("/"),
          category: node,
          pathSlugs,
          pathNames,
          searchText: normalizeText(
            `${node.name ?? ""} ${node.nameDe ?? ""} ${node.nameEn ?? ""} ${pathNames.join(" ")}`
          ),
          labelNormalized: normalizeText(node.name),
          pathNormalized: normalizeText(pathNames.join(" ")),
        });

        if (node.subcategories?.length) {
          collect(node.subcategories, pathSlugs, pathNames);
        }
      });
    };

    collect(thematicCategories, [], []);
    return options;
  }, [thematicCategories]);

  const activePathNormalized = useMemo(
    () => normalizeText(categoryPath.join(" ")),
    [categoryPath]
  );

  const thematicSearchResults = useMemo(() => {
    const term = normalizeText(thematicQuery.trim());
    if (!term) return [];

    const matches = thematicSearchOptions
      .map((option) => {
        const hitIndex = option.searchText.indexOf(term);
        if (hitIndex === -1) return null;
        const startsWith = option.labelNormalized.startsWith(term) ? 2 : 0;
        const branchBonus =
          !!activePathNormalized &&
          option.pathNormalized.includes(activePathNormalized)
            ? 0.5
            : 0;
        const positionScore = hitIndex === 0 ? 1 : 0.5;
        return {
          option,
          score: startsWith + branchBonus + positionScore,
        };
      })
      .filter(Boolean) as Array<{ option: ThematicSearchOption; score: number }>;

    matches.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.option.pathNames.join(" > ").localeCompare(b.option.pathNames.join(" > "));
    });

    return matches.slice(0, 8).map((entry) => entry.option);
  }, [activePathNormalized, thematicQuery, thematicSearchOptions]);

  const handleThematicSearchSelect = (pathSlugs: string[]) => {
    setThematicQuery("");
    handleCategoryPathSelect(pathSlugs);
  };

  return (
    <section
      className={`space-y-4 flex flex-col min-h-0 ${
        constrainedSection ? "overflow-hidden" : ""
      }`}
      style={
        constrainedSection
          ? { maxHeight: `${Math.floor(sectionMaxHeightPx)}px` }
          : undefined
      }
    >
      <div className="mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="icon-box">
              <FolderTree className="w-5 h-5 icon-primary" style={iconStyle} />
            </div>
            <div className="space-y-1">
              {categoryPath.length === 0 ? (
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold" style={titleTextStyle}>
                    {t("categories.thematicFilterHeading")}
                  </h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t("categories.browseDescription")}
                        className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 dark:focus-visible:ring-offset-slate-900"
                        style={iconStyle}
                      >
                        ?
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="center"
                      sideOffset={6}
                      className="max-w-[min(90vw,20rem)] whitespace-normal text-left leading-relaxed break-words"
                    >
                      {t("categories.browseDescription")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleBreadcrumbClick(categoryPath.length - 2)
                      }
                      className="gap-2 px-3 py-1.5 rounded-full bg-blue-50/80 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-900/50 hover:bg-blue-100 dark:hover:bg-blue-900/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                      style={titleTextStyle}
                    >
                      <ArrowLeft className="w-4 h-4" style={iconStyle} />
                      {t("common.back")}
                    </Button>
                    <div>
                      <h3 className="text-lg font-semibold" style={titleTextStyle}>
                        {categoryPath[categoryPath.length - 1]}
                      </h3>
                      <p className="text-sm text-secondary" style={bodyTextStyle}>
                        {(currentCategory?.count || 0) +
                          " " +
                          t("categories.websites")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewResults}
            className="gap-2 px-3 py-1.5 rounded-full border border-[var(--action-border)] bg-[var(--action-bg)] hover:bg-[var(--action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
            style={{ ...actionButtonStyle, ...titleTextStyle }}
          >
            <ChevronsDown className="w-4 h-4" style={iconStyle} />
            {t("categories.showResults")}
          </Button>
        </div>
      </div>

      {categoryPath.length > 0 && (
        <div className="mb-6">
          <Breadcrumb>
            <BreadcrumbList className="max-w-full">
              <BreadcrumbItem className="min-w-0">
                <BreadcrumbLink
                  onClick={() => handleBreadcrumbClick(-1)}
                  className="cursor-pointer text-[var(--breadcrumb-text)] hover:text-[var(--breadcrumb-hover)]"
                  style={breadcrumbStyle}
                >
                  {t("categories.title")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              {categoryPath.map((category, index) => (
                <Fragment key={`${category}-${index}`}>
                  <li
                    aria-hidden="true"
                    className="mx-1 shrink-0"
                    style={{
                      color: "var(--breadcrumb-text)",
                      fontSize: "0.95em",
                      lineHeight: 1,
                      transform: "translateY(0.28em)",
                    }}
                  >
                    &gt;
                  </li>
                  <BreadcrumbItem className="min-w-0">
                    {index === categoryPath.length - 1 ? (
                      <BreadcrumbPage
                        className="text-[var(--breadcrumb-text)]"
                        style={breadcrumbStyle}
                      >
                        {category}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => handleBreadcrumbClick(index)}
                        className="cursor-pointer text-[var(--breadcrumb-text)] hover:text-[var(--breadcrumb-hover)]"
                        style={breadcrumbStyle}
                      >
                        {category}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      )}

      {/* TEMPORARY: easy-to-remove thematic quick search (mirrors region search UX). */}
      <div>
        <div className="relative">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary"
            style={iconStyle}
          />
          <Input
            value={thematicQuery}
            onChange={(e) => setThematicQuery(e.target.value)}
            placeholder={t("categories.thematicSearchPlaceholder")}
            className="pl-9 text-sm"
            disabled={isLoading || thematicSearchOptions.length === 0}
          />
        </div>
        {thematicQuery.trim() ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {thematicSearchResults.length === 0 ? (
              <div className="px-3 py-2 text-xs text-secondary" style={bodyTextStyle}>
                {t("categories.thematicSearchNoResults")}
              </div>
            ) : (
              thematicSearchResults.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleThematicSearchSelect(option.pathSlugs)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex flex-col gap-0.5"
                >
                  <span className="text-sm font-semibold" style={titleTextStyle}>
                    {option.category.name}
                  </span>
                  <span className="text-xs text-secondary" style={bodyTextStyle}>
                    {(option.category.count ?? 0) +
                      " " +
                      t("categories.websites") +
                      (option.pathNames.length > 1
                        ? ` • ${option.pathNames.join(" > ")}`
                        : "")}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      <div
        className={
          constrainedSection
            ? "overflow-y-auto overscroll-contain pr-1 min-h-0 flex-1"
            : "overflow-y-auto overscroll-contain pr-1"
        }
        style={constrainedSection ? undefined : stackedMobileListStyle}
      >
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Card key={`thematic-skeleton-${idx}`} className="feature-card">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <Skeleton className="h-10 w-10 rounded-xl" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                  <Skeleton className="mt-4 h-5 w-3/4" />
                </CardHeader>
              </Card>
            ))}
          </div>
        )}

        {!isLoading &&
          (showingSubcategories || showLeafInfo) &&
          (currentSubcategories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {currentSubcategories.map((subcat, idx) => {
            const Icon = categoryIconMap[subcat.iconName ?? ""] ?? FolderTree;
            return (
              <Card
                key={subcat.slug || idx}
                className="feature-card"
                onClick={() => {
                  handleCategoryClick(subcat.slug);
                }}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="icon-box">
                      <Icon className="w-6 h-6 icon-primary" style={iconStyle} />
                    </div>
                    <Badge variant="secondary">
                      {subcat.count ?? 0} {t("categories.websites")}
                    </Badge>
                  </div>
                  <CardTitle
                    className="mt-4 card-title-text"
                    style={titleTextStyle}
                  >
                    {subcat.name}
                  </CardTitle>
                </CardHeader>
              </Card>
            );
          })}
            </div>
          ) : (
            <Card className="stat-card">
              <CardContent className="py-10 text-center space-y-4">
                <p className="text-lg font-semibold" style={titleTextStyle}>
                  {leafTitle}
                </p>
                <p className="text-sm text-secondary" style={bodyTextStyle}>
                  {leafDescription}
                </p>
              </CardContent>
            </Card>
          ))}

        {!isLoading && !showingSubcategories && !showLeafInfo && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {thematicCategories.map((category, idx) => {
              const Icon = categoryIconMap[category.iconName ?? ""] ?? FolderTree;
              return (
                <Card
                  key={category.slug || idx}
                  className="feature-card"
                  data-icon-name={category.iconName || undefined}
                  onClick={() => handleCategoryClick(category.slug)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="icon-box">
                        <Icon className="w-6 h-6 icon-primary" style={iconStyle} />
                      </div>
                      <Badge variant="secondary">
                        {category.count ?? 0} {t("categories.websites")}
                      </Badge>
                    </div>
                    <CardTitle
                      className="mt-4 card-title-text"
                      style={titleTextStyle}
                    >
                      {category.name}
                    </CardTitle>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
